/**
 * CMP UI（同意バナー・設定モーダルのDOM生成・スタイル挿入）
 *
 * CSSはJS内にテンプレートリテラルとして保持し、初期化時に<style>タグを生成して<head>に挿入。
 * BEM命名でスコープ確保、CSS Custom Propertiesでカスタマイズ可能。
 */

const styles = `
.cmp-banner {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: var(--cmp-z-index, 10000);
  background: var(--cmp-bg, #fff);
  color: var(--cmp-text, #333);
  font-family: var(--cmp-font-family, inherit);
  box-shadow: 0 -2px 16px rgba(0, 0, 0, 0.12);
  padding: 0;
  box-sizing: border-box;
}

.cmp-banner:focus {
  outline: none;
}

.cmp-banner__inner {
  max-width: var(--cmp-max-width, 640px);
  margin: 0 auto;
  padding: 20px 24px;
}

.cmp-banner__title {
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 8px;
  line-height: 1.4;
}

.cmp-banner__description {
  font-size: 14px;
  line-height: 1.6;
  margin: 0 0 16px;
  color: var(--cmp-text-secondary, #666);
}

.cmp-banner__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.cmp-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: var(--cmp-radius, 8px);
  cursor: pointer;
  font-size: 14px;
  font-family: var(--cmp-font-family, inherit);
  font-weight: 500;
  padding: 10px 20px;
  line-height: 1;
  transition: background-color 0.15s ease;
  white-space: nowrap;
}

.cmp-btn:focus-visible {
  outline: 2px solid var(--cmp-primary, #106EB8);
  outline-offset: 2px;
}

.cmp-btn--primary {
  background: var(--cmp-primary, #106EB8);
  color: #fff;
}

.cmp-btn--primary:hover {
  background: var(--cmp-primary-hover, #0C5B9B);
}

.cmp-btn--secondary {
  background: var(--cmp-border, #e5e7eb);
  color: var(--cmp-text, #333);
}

.cmp-btn--secondary:hover {
  background: #d1d5db;
}

.cmp-btn--link {
  background: none;
  color: var(--cmp-secondary, #6b7280);
  padding: 10px 12px;
  text-decoration: underline;
}

.cmp-btn--link:hover {
  color: var(--cmp-secondary-hover, #4b5563);
}

/* 設定モーダル */
.cmp-overlay {
  position: fixed;
  inset: 0;
  z-index: calc(var(--cmp-z-index, 10000) + 1);
  background: var(--cmp-overlay, rgba(0, 0, 0, 0.5));
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.cmp-modal {
  background: var(--cmp-bg, #fff);
  color: var(--cmp-text, #333);
  font-family: var(--cmp-font-family, inherit);
  border-radius: var(--cmp-radius, 8px);
  max-width: var(--cmp-max-width, 640px);
  width: 100%;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}

.cmp-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 0;
  flex-shrink: 0;
}

.cmp-modal__title {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  line-height: 1.4;
}

.cmp-modal__close {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 24px;
  color: var(--cmp-text-secondary, #666);
  padding: 4px;
  line-height: 1;
  font-family: var(--cmp-font-family, inherit);
}

.cmp-modal__close:hover {
  color: var(--cmp-text, #333);
}

.cmp-modal__close:focus-visible {
  outline: 2px solid var(--cmp-primary, #106EB8);
  outline-offset: 2px;
}

.cmp-modal__body {
  overflow-y: auto;
  padding: 16px 24px;
  flex: 1;
}

.cmp-modal__footer {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 16px 24px 20px;
  border-top: 1px solid var(--cmp-border, #e5e7eb);
  flex-shrink: 0;
}

/* カテゴリリスト */
.cmp-category {
  padding: 16px 0;
  border-bottom: 1px solid var(--cmp-border, #e5e7eb);
}

.cmp-category:last-child {
  border-bottom: none;
}

.cmp-category__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.cmp-category__info {
  flex: 1;
  min-width: 0;
}

.cmp-category__title {
  font-size: 14px;
  font-weight: 600;
  margin: 0;
  line-height: 1.4;
}

.cmp-category__description {
  font-size: 13px;
  color: var(--cmp-text-secondary, #666);
  margin: 4px 0 0;
  line-height: 1.5;
}

/* トグルスイッチ */
.cmp-toggle {
  position: relative;
  width: 44px;
  height: 24px;
  flex-shrink: 0;
  background: #b0b5bd;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  padding: 0;
  transition: background-color 0.2s ease;
}

.cmp-toggle:focus-visible {
  outline: 2px solid var(--cmp-primary, #106EB8);
  outline-offset: 2px;
}

.cmp-toggle[aria-checked="true"] {
  background: var(--cmp-primary, #106EB8);
}

.cmp-toggle::after {
  content: '';
  position: absolute;
  top: 2px;
  left: 2px;
  width: 20px;
  height: 20px;
  background: #fff;
  border-radius: 50%;
  transition: transform 0.2s ease;
}

.cmp-toggle[aria-checked="true"]::after {
  transform: translateX(20px);
}

.cmp-toggle[disabled] {
  opacity: 0.6;
  cursor: not-allowed;
}

/* 同意撤回エリア */
.cmp-modal__revoke {
  padding: 0 24px 16px;
  text-align: center;
  flex-shrink: 0;
}

/* バナー非表示 */
.cmp-banner--hidden {
  display: none;
}

/* リビジットボタン */
.cmp-revisit {
  position: fixed;
  bottom: 16px;
  left: 16px;
  z-index: var(--cmp-z-index, 10000);
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background: var(--cmp-primary, #106EB8);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  transition: background-color 0.15s ease, transform 0.15s ease;
}

.cmp-revisit:hover {
  background: var(--cmp-primary-hover, #0C5B9B);
  transform: scale(1.1);
}

.cmp-revisit:focus-visible {
  outline: 2px solid var(--cmp-primary, #106EB8);
  outline-offset: 2px;
}

.cmp-revisit__icon {
  width: 20px;
  height: 20px;
  fill: currentColor;
  pointer-events: none;
}
`;

/**
 * CSSスタイルを<head>に挿入する
 */
export function injectStyles() {
  if (document.querySelector('style[data-cmp]')) return; // 既に挿入済み
  const style = document.createElement('style');
  style.setAttribute('data-cmp', '');
  style.textContent = styles;
  document.head.appendChild(style);
}

/**
 * 同意バナーを生成する
 * @param {Object} texts - 言語解決済みのテキスト
 * @param {Object} callbacks - { onAcceptAll, onAcceptNecessary, onShowPreferences }
 * @returns {HTMLElement}
 */
export function createBanner(texts, callbacks) {
  const cm = texts.consentModal || {};
  const banner = document.createElement('div');
  banner.className = 'cmp-banner';
  banner.setAttribute('role', 'dialog');
  banner.setAttribute('aria-label', cm.title || 'Cookie Consent');
  banner.setAttribute('tabindex', '-1');

  banner.innerHTML = `
    <div class="cmp-banner__inner">
      <p class="cmp-banner__title">${escapeHtml(cm.title || '')}</p>
      <p class="cmp-banner__description">${escapeHtmlWithBreaks(cm.description || '')}</p>
      <div class="cmp-banner__actions">
        <button type="button" class="cmp-btn cmp-btn--primary" data-cmp-role="accept-all">
          ${escapeHtml(cm.acceptAllBtn || 'Accept All')}
        </button>
        <button type="button" class="cmp-btn cmp-btn--primary" data-cmp-role="accept-necessary">
          ${escapeHtml(cm.acceptNecessaryBtn || 'Necessary Only')}
        </button>
        <button type="button" class="cmp-btn cmp-btn--link" data-cmp-role="show-preferences">
          ${escapeHtml(cm.showPreferencesBtn || 'Manage Preferences')}
        </button>
      </div>
    </div>
  `;

  banner.querySelector('[data-cmp-role="accept-all"]').addEventListener('click', callbacks.onAcceptAll);
  banner.querySelector('[data-cmp-role="accept-necessary"]').addEventListener('click', callbacks.onAcceptNecessary);
  banner.querySelector('[data-cmp-role="show-preferences"]').addEventListener('click', callbacks.onShowPreferences);

  return banner;
}

/**
 * 設定モーダルを生成する
 * @param {Object} texts - 言語解決済みのテキスト
 * @param {Array} categories - カテゴリ定義配列
 * @param {Object} currentConsent - 現在の同意状態 { necessary: true, analytics: false }
 * @param {Object} callbacks - { onSave, onAcceptAll, onAcceptNecessary, onRevoke, onClose }
 * @returns {HTMLElement}
 */
export function createPreferencesModal(texts, categories, currentConsent, callbacks) {
  const pm = texts.preferencesModal || {};
  const categoryTexts = pm.categories || {};

  const overlay = document.createElement('div');
  overlay.className = 'cmp-overlay';

  const categoriesHtml = categories
    .map((cat) => {
      const catText = categoryTexts[cat.key] || {};
      const isChecked = cat.required ? true : Boolean(currentConsent[cat.key]);
      const isDisabled = cat.required;

      return `
      <div class="cmp-category">
        <div class="cmp-category__header">
          <div class="cmp-category__info">
            <p class="cmp-category__title">${escapeHtml(catText.title || cat.key)}</p>
            ${
              catText.description
                ? `<p class="cmp-category__description">${escapeHtmlWithBreaks(catText.description)}</p>`
                : ''
            }
          </div>
          <button type="button"
            class="cmp-toggle"
            role="switch"
            aria-checked="${isChecked}"
            aria-label="${escapeHtml(catText.title || cat.key)}"
            data-cmp-category-key="${escapeHtml(cat.key)}"
            ${isDisabled ? 'disabled' : ''}>
          </button>
        </div>
      </div>
    `;
    })
    .join('');

  overlay.innerHTML = `
    <div class="cmp-modal" role="dialog" aria-modal="true" aria-labelledby="cmp-modal-title">
      <div class="cmp-modal__header">
        <h2 class="cmp-modal__title" id="cmp-modal-title">${escapeHtml(pm.title || 'Cookie Preferences')}</h2>
        <button type="button" class="cmp-modal__close" aria-label="${escapeHtml(
          pm.closeLabel || 'Close'
        )}">&times;</button>
      </div>
      <div class="cmp-modal__body">
        ${categoriesHtml}
      </div>
      <div class="cmp-modal__footer">
        <button type="button" class="cmp-btn cmp-btn--primary" data-cmp-role="save">
          ${escapeHtml(pm.saveBtn || 'Allow selected')}
        </button>
        <button type="button" class="cmp-btn cmp-btn--primary" data-cmp-role="accept-all">
          ${escapeHtml(pm.acceptAllBtn || 'Accept All')}
        </button>
        <button type="button" class="cmp-btn cmp-btn--primary" data-cmp-role="accept-necessary">
          ${escapeHtml(pm.acceptNecessaryBtn || 'Necessary Only')}
        </button>
      </div>
      ${
        callbacks.onRevoke
          ? `<div class="cmp-modal__revoke">
               <button type="button" class="cmp-btn cmp-btn--link" data-cmp-role="revoke">
                 ${escapeHtml(pm.revokeConsentBtn || 'Withdraw consent')}
               </button>
             </div>`
          : ''
      }
    </div>
  `;

  // トグルスイッチのクリック処理
  overlay.querySelectorAll('.cmp-toggle:not([disabled])').forEach((toggle) => {
    toggle.addEventListener('click', () => {
      const checked = toggle.getAttribute('aria-checked') === 'true';
      toggle.setAttribute('aria-checked', String(!checked));
    });
  });

  // 閉じるボタン
  overlay.querySelector('.cmp-modal__close').addEventListener('click', callbacks.onClose);

  // 保存ボタン
  overlay.querySelector('[data-cmp-role="save"]').addEventListener('click', () => {
    const selections = {};
    overlay.querySelectorAll('.cmp-toggle').forEach((toggle) => {
      const key = toggle.getAttribute('data-cmp-category-key');
      selections[key] = toggle.getAttribute('aria-checked') === 'true';
    });
    callbacks.onSave(selections);
  });

  // すべて許可ボタン
  overlay.querySelector('[data-cmp-role="accept-all"]').addEventListener('click', callbacks.onAcceptAll);

  // 必須のみ許可ボタン
  overlay.querySelector('[data-cmp-role="accept-necessary"]').addEventListener('click', callbacks.onAcceptNecessary);

  // 同意撤回ボタン（同意済みの場合のみ表示）
  const revokeBtn = overlay.querySelector('[data-cmp-role="revoke"]');
  if (revokeBtn && callbacks.onRevoke) {
    revokeBtn.addEventListener('click', callbacks.onRevoke);
  }

  // オーバーレイクリックで閉じる
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) callbacks.onClose();
  });

  // Escapeキーで閉じる
  overlay.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      callbacks.onClose();
    }
  });

  // フォーカストラップ
  setupFocusTrap(overlay);

  return overlay;
}

/**
 * リビジットボタン（同意再設定用）を生成する
 * @param {Function} callback - クリック時のコールバック
 * @param {Object} [texts] - 言語解決済みのテキスト
 * @returns {HTMLElement}
 */
export function createRevisitButton(callback, texts) {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'cmp-revisit';
  const revisitLabel = (texts && texts.revisitLabel) || 'Cookie preferences';
  btn.setAttribute('aria-label', revisitLabel);

  btn.innerHTML = `
    <svg class="cmp-revisit__icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 2.18l7 3.12v4.7c0 4.83-3.23 9.36-7 10.58-3.77-1.22-7-5.75-7-10.58V6.3l7-3.12z"/>
    </svg>
  `;

  btn.addEventListener('click', callback);

  return btn;
}

/**
 * モーダル内のフォーカストラップを設定する
 * @param {HTMLElement} container
 */
function setupFocusTrap(container) {
  container.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab') return;

    const focusable = container.querySelectorAll(
      'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  });
}

/**
 * HTMLエスケープ
 * @param {string} str
 * @returns {string}
 */
function escapeHtml(str) {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

/**
 * HTMLエスケープ後に改行を<br>に変換する
 * @param {string} str
 * @returns {string}
 */
function escapeHtmlWithBreaks(str) {
  return escapeHtml(str).replace(/\n/g, '<br>');
}
